import { notification } from 'antd';
import { AppSearch, Inspection } from '@/types';
import { LocalStorageKeys } from '@/utils/constants';
import {
  downloadFile,
  getLocalStorageValue,
  setLocalStorageValue,
} from '@/utils/helpers';
import type { IDownloadTypes } from '@/ui/components/CustomTable/exportToFile';
import { prepareInspectionVehiclesSearchBody } from '@/api/Inspections';
import { exportsApi, type ExportJobState } from './Exports';

const POLL_MS = 2000;
const EXPORT_NOTICE_KEY = 'inspections-export';

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const exportTitle = (includeDamages: boolean) =>
  includeDamages ? 'Inspection & Damages' : 'Inspections';

export const describeExportProgress = (
  job: Pick<
    ExportJobState,
    'status' | 'processedCount' | 'totalCount' | 'error'
  >,
) => {
  if (job.status === 'Queued') {
    if (job.totalCount != null && job.totalCount > 0) {
      return `Queued. 0 of ${job.totalCount.toLocaleString()} inspections…`;
    }
    return 'Queued. Preparing export…';
  }
  if (job.status === 'Ready') return 'Complete. Starting download…';
  if (job.status === 'Failed')
    return job.error || 'Export failed. Please try again.';

  const processed = job.processedCount ?? 0;
  if (job.totalCount != null && job.totalCount > 0) {
    return `Exporting ${processed.toLocaleString()} of ${job.totalCount.toLocaleString()} inspections…`;
  }
  if (processed > 0) {
    return `Exporting ${processed.toLocaleString()} inspections…`;
  }
  return 'Exporting inspections…';
};

const showExportNotice = (
  title: string,
  description: string,
  type: 'info' | 'success' | 'error' = 'info',
) => {
  const open = type === 'info' ? notification.open : notification[type];
  open({
    key: EXPORT_NOTICE_KEY,
    message: title,
    description,
    duration: type === 'info' ? 0 : 4,
    placement: 'bottomRight',
  });
};

export const waitForExportJob = async (
  exportId: string,
  onProgress?: (job: ExportJobState) => void,
): Promise<ExportJobState> => {
  for (;;) {
    const response = await exportsApi.getExportJob(exportId);
    const job = response.data;
    if ((response as { errorResponse?: boolean }).errorResponse || !job) {
      if ((response as { code?: number }).code === 404) {
        throw new Error('Unable to check export status.');
      }
      await delay(POLL_MS);
      continue;
    }
    onProgress?.(job);
    if (job.status === 'Ready') return job;
    if (job.status === 'Failed') {
      throw new Error(job.error || 'Export failed. Please try again.');
    }
    await delay(POLL_MS);
  }
};

export const downloadExportFiles = (job: ExportJobState) => {
  job.files?.forEach((file) => {
    if (file.downloadUrl) downloadFile(file.downloadUrl, file.fileName);
  });
};

export type PendingInspectionsExport = {
  exportId: string;
  title: string;
  totalCount?: number;
};

const followingExportIds = new Set<string>();

const parsePendingInspectionsExport = (
  value: unknown,
): PendingInspectionsExport | null => {
  if (!value || typeof value !== 'object') return null;
  const { exportId, title, totalCount } = value as PendingInspectionsExport;
  if (typeof exportId !== 'string' || !exportId) return null;
  if (typeof title !== 'string' || !title) return null;
  return {
    exportId,
    title,
    totalCount: typeof totalCount === 'number' ? totalCount : undefined,
  };
};

export const readPendingInspectionsExport = () =>
  parsePendingInspectionsExport(
    getLocalStorageValue<unknown>(
      LocalStorageKeys.PendingInspectionsExport,
      null,
    ),
  );

const writePendingInspectionsExport = (pending: PendingInspectionsExport) => {
  setLocalStorageValue(LocalStorageKeys.PendingInspectionsExport, pending);
};

const clearPendingInspectionsExport = (exportId: string) => {
  if (readPendingInspectionsExport()?.exportId !== exportId) return;
  localStorage.removeItem(LocalStorageKeys.PendingInspectionsExport);
};

const followInspectionsExport = async (pending: PendingInspectionsExport) => {
  if (followingExportIds.has(pending.exportId)) return;
  followingExportIds.add(pending.exportId);
  writePendingInspectionsExport(pending);

  const { exportId, title, totalCount } = pending;
  showExportNotice(
    title,
    describeExportProgress({
      status: 'Queued',
      processedCount: 0,
      totalCount,
    }),
  );
  try {
    const job = await waitForExportJob(exportId, (state) => {
      showExportNotice(
        title,
        describeExportProgress({
          ...state,
          totalCount: state.totalCount ?? totalCount,
        }),
      );
    });
    downloadExportFiles(job);
    showExportNotice(title, 'Complete. Starting download…', 'success');
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : 'Export failed. Please try again.';
    showExportNotice(title, message, 'error');
    throw error;
  } finally {
    followingExportIds.delete(exportId);
    clearPendingInspectionsExport(exportId);
  }
};

export const resumePendingInspectionsExport = async () => {
  const pending = readPendingInspectionsExport();
  if (!pending) return;
  try {
    await followInspectionsExport(pending);
  } catch {
    // Progress notice already reflects the failure.
  }
};

export const runInspectionsExport = async (
  query: AppSearch<Inspection>,
  format: IDownloadTypes,
  includeDamages = false,
  totalCount?: number,
) => {
  const title = exportTitle(includeDamages);
  const knownTotal = totalCount && totalCount > 0 ? totalCount : undefined;
  showExportNotice(
    title,
    describeExportProgress({
      status: 'Queued',
      processedCount: 0,
      totalCount: knownTotal,
    }),
  );
  const prepared = prepareInspectionVehiclesSearchBody(query);
  const body = {
    filters: prepared.filters,
    search: prepared.search,
    sortField: prepared.sortField,
    sortOrder: prepared.sortOrder,
    includeDamages,
    totalCount: knownTotal,
    format,
  };
  const started = await exportsApi.startInspectionsExport(body);
  if (
    (started as { errorResponse?: boolean }).errorResponse ||
    !started.data?.exportId
  ) {
    showExportNotice(title, 'Unable to start export.', 'error');
    throw new Error('Unable to start export.');
  }
  await followInspectionsExport({
    exportId: started.data.exportId,
    title,
    totalCount: started.data.totalCount ?? knownTotal,
  });
};
